import { useState } from "react";
import type { Plan, Site } from "../lib/database.types";
import { useSites } from "../lib/sites";
import { ApiError } from "../lib/api";
import type { Lang } from "../lib/prefs";
import { PLAN_COPY, PLAN_ORDER, PLAN_PRICE_MXN, mxn } from "../lib/plans";

// M8 — Upgrade modal + checkout flow (ported from the dashboard mockup's upgrade
// modal). Three steps for ONE target site: pick a plan → stub checkout (email /
// RFC / Tarjeta·SPEI·OXXO — no processor) → success. Confirm calls the existing
// PATCH /api/sites/:id (via useSites().updateSite) to set plan + cadence + trial;
// NO real charge. Respects the 1-free-site/account rule (server-enforced too).

type PayMethod = "card" | "spei" | "oxxo";
type Step = "select" | "checkout" | "success";

const T = {
  es: {
    title: "Cambiar plan",
    forSite: "para",
    sub: "Precios por sitio, en MXN. Sin permanencia · cancela cuando quieras · factura CFDI 4.0 automática.",
    current: "Plan actual",
    choose: "Elegir",
    freeUsed: "Ya tienes un sitio Gratis en tu cuenta; elige un plan de pago.",
    // checkout
    coTitle: "Confirmar",
    coSub: "Sin cargos reales — la facturación está en modo de prueba.",
    email: "Email",
    rfc: "RFC (para CFDI)",
    rfcPh: "XAXX010101000",
    payM: "Método de pago",
    card: "Tarjeta",
    spei: "SPEI",
    oxxo: "OXXO",
    trialNote: "Prueba de 30 días — no se cobra hoy.",
    back: "Atrás",
    confirm: "Confirmar cambio",
    confirming: "Procesando…",
    // success
    doneTitle: "¡Listo!",
    doneBody: (plan: string, domain: string) => `${domain} ahora está en el plan ${plan}.`,
    doneTrial: "Tu prueba de 30 días ha comenzado. No se realizó ningún cargo.",
    close: "Cerrar",
    cancel: "Cancelar",
    errFree: "Ya tienes un sitio Gratis; elige un plan de pago.",
    errGeneric: "No se pudo cambiar el plan. Intenta de nuevo.",
    perMo: "/mes",
    free0: "$0",
  },
  en: {
    title: "Change plan",
    forSite: "for",
    sub: "Per-site pricing, in MXN. No commitment · cancel anytime · automatic CFDI 4.0 invoice.",
    current: "Current plan",
    choose: "Choose",
    freeUsed: "Your account already has a Free site; pick a paid plan.",
    coTitle: "Confirm",
    coSub: "No real charges — billing is in test mode.",
    email: "Email",
    rfc: "Tax ID (RFC, for CFDI)",
    rfcPh: "XAXX010101000",
    payM: "Payment method",
    card: "Card",
    spei: "SPEI",
    oxxo: "OXXO",
    trialNote: "30-day trial — nothing charged today.",
    back: "Back",
    confirm: "Confirm change",
    confirming: "Processing…",
    doneTitle: "Done!",
    doneBody: (plan: string, domain: string) => `${domain} is now on the ${plan} plan.`,
    doneTrial: "Your 30-day trial has started. No charge was made.",
    close: "Close",
    cancel: "Cancel",
    errFree: "You already have a Free site; pick a paid plan.",
    errGeneric: "Could not change the plan. Please try again.",
    perMo: "/mo",
    free0: "$0",
  },
};

export default function UpgradeModal({
  lang,
  site,
  email,
  onClose,
}: {
  lang: Lang;
  site: Site;
  email: string;
  onClose: () => void;
}) {
  const t = T[lang];
  const copy = PLAN_COPY[lang];
  const { sites, updateSite } = useSites();

  const [step, setStep] = useState<Step>("select");
  const [chosen, setChosen] = useState<Plan | null>(null);
  const [rfc, setRfc] = useState("");
  const [pay, setPay] = useState<PayMethod>("card");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Free is only selectable if THIS site is already free, or the account has no
  // other free site (mirrors the server's one-free-site cap).
  const hasOtherFreeSite = sites.some((s) => s.plan === "free" && s.id !== site.id);

  function pick(plan: Plan) {
    if (plan === site.plan) return; // no-op: already on it
    if (plan === "free") return; // downgrade-to-free handled elsewhere; not a checkout
    setChosen(plan);
    setErr(null);
    setStep("checkout");
  }

  async function confirm() {
    if (!chosen) return;
    setBusy(true);
    setErr(null);
    try {
      await updateSite(site.id, { plan: chosen });
      setStep("success");
    } catch (e) {
      if (e instanceof ApiError && e.code === "free_site_exists") setErr(t.errFree);
      else setErr(t.errGeneric);
    } finally {
      setBusy(false);
    }
  }

  const chosenIsTrial = chosen === "pro";

  return (
    <>
      <div className="scrim on" onClick={onClose} aria-hidden="true" />
      <div
        className="modal on"
        role="dialog"
        aria-modal="true"
        aria-label={t.title}
        style={{ maxWidth: step === "select" ? 620 : 460 }}
      >
        <div className="modal-h">
          <h3>
            {step === "success"
              ? t.doneTitle
              : step === "checkout"
                ? t.coTitle
                : t.title}
          </h3>
          <button className="modal-x" onClick={onClose} aria-label={t.close}>
            ✕
          </button>
        </div>

        <div className="modal-b">
          {step === "select" && (
            <>
              <p className="sub-line" style={{ marginTop: 0 }}>
                {t.title} {t.forSite} <b>{site.domain}</b>. {t.sub}
              </p>
              {hasOtherFreeSite && <p className="hint">{t.freeUsed}</p>}
              <div className="tiers">
                {PLAN_ORDER.map((plan) => {
                  const price = PLAN_PRICE_MXN[plan];
                  const isCurrent = plan === site.plan;
                  const freeBlocked = plan === "free" && hasOtherFreeSite;
                  const disabled = isCurrent || freeBlocked;
                  return (
                    <div key={plan} className={`tier${plan === "pro" ? " hi" : ""}`}>
                      <div className="nm">{copy[plan].name}</div>
                      <div className="pr">
                        {price === 0 ? t.free0 : mxn(price)}{" "}
                        {price > 0 && <small>{copy[plan].suffix}</small>}
                      </div>
                      <ul>
                        {copy[plan].features.map((f) => (
                          <li key={f}>
                            <span className="ck">✓</span> <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                      <button
                        className={`btn btn-block ${plan === "pro" ? "btn-primary" : "btn-ghost"}`}
                        disabled={disabled}
                        onClick={() => pick(plan)}
                      >
                        {isCurrent ? t.current : `${t.choose} ${copy[plan].name}`}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {step === "checkout" && chosen && (
            <>
              <p className="sub-line" style={{ marginTop: 0 }}>
                {copy[chosen].name} · {mxn(PLAN_PRICE_MXN[chosen])} {t.perMo} · {site.domain}
              </p>
              {err && <p className="auth-err">{err}</p>}

              <div className="fld">
                <label>{t.email}</label>
                <input className="inp" value={email} disabled />
              </div>
              <div className="fld">
                <label>{t.rfc}</label>
                <input
                  className="inp"
                  value={rfc}
                  onChange={(e) => setRfc(e.target.value.toUpperCase())}
                  placeholder={t.rfcPh}
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={13}
                />
              </div>
              <div className="fld">
                <label>{t.payM}</label>
                <div className="paypick">
                  {(["card", "spei", "oxxo"] as PayMethod[]).map((m) => (
                    <button
                      type="button"
                      key={m}
                      className={`payopt${pay === m ? " on" : ""}`}
                      onClick={() => setPay(m)}
                    >
                      {t[m]}
                    </button>
                  ))}
                </div>
              </div>

              {chosenIsTrial && (
                <p className="sub-line" style={{ margin: "12px 0 0" }}>
                  <b>✓</b> {t.trialNote}
                </p>
              )}
              <p className="sub-line" style={{ margin: "6px 0 0" }}>
                {t.coSub}
              </p>

              <div className="modal-foot">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setStep("select")}
                  disabled={busy}
                >
                  {t.back}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={confirm}
                  disabled={busy}
                >
                  {busy ? t.confirming : t.confirm}
                </button>
              </div>
            </>
          )}

          {step === "success" && chosen && (
            <>
              <div className="checkok" aria-hidden="true">
                ✓
              </div>
              <p style={{ textAlign: "center", fontWeight: 600, margin: "4px 0 6px" }}>
                {t.doneBody(copy[chosen].name, site.domain)}
              </p>
              {chosenIsTrial && (
                <p className="sub-line" style={{ textAlign: "center", margin: "0 0 4px" }}>
                  {t.doneTrial}
                </p>
              )}
              <div className="modal-foot" style={{ justifyContent: "center" }}>
                <button type="button" className="btn btn-primary" onClick={onClose}>
                  {t.close}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
